<template>
    <div>
        <div class="ui-list-bar">
            <ui-pills v-model="statusTab" :items="pillItems" />
            <v-btn outlined :loading="reconciling" @click="reconcile"><v-icon left small>mdi-sync</v-icon>Conferir com o QGC</v-btn>
        </div>

        <div class="ui-table-card">
            <div class="ui-toolbar">
                <v-text-field v-model="filter.search" placeholder="Buscar por credor, CPF/CNPJ ou código do termo" dense outlined hide-details
                    prepend-inner-icon="mdi-magnify" class="ui-toolbar__search"></v-text-field>
                <v-select v-model="filter.class_type" :items="classItems" placeholder="Todas as classes" dense outlined hide-details
                    class="ui-toolbar__field" :menu-props="{ offsetY: true }" @change="reload"></v-select>
                <v-select v-model="filter.signer_kind" :items="signerItems" placeholder="PF e PJ" dense outlined hide-details
                    class="ui-toolbar__field" :menu-props="{ offsetY: true }" @change="reload"></v-select>
                <v-switch v-model="filter.current" label="Só versão vigente" dense hide-details inset @change="reload"></v-switch>
                <v-btn text class="ui-toolbar__clear" :disabled="!hasFilters" @click="clearFilters"><v-icon left small>mdi-restore</v-icon>Limpar</v-btn>
            </div>
            <v-data-table :headers="headers" :items="visibleItems" :loading="loading" disable-sort hide-default-footer
                :page.sync="page" :items-per-page="itemsPerPage"
                no-data-text="Nenhum termo encontrado" loading-text="Carregando..." class="ui-clickable-rows" @click:row="open">
                <template v-slot:[`item.creditor`]="{ item }">
                    <div class="ui-cell-main">{{ item.credit_snapshot.name }}</div>
                    <div class="ui-cell-sub">{{ formatRegistration(item.credit_snapshot.registration) || "—" }}<template v-if="item.signer_kind === 'pj'"> · PJ</template><template v-if="item.procurator_association_id"> · via procurador</template></div>
                </template>
                <template v-slot:[`item.class_option`]="{ item }">
                    <div class="ui-cell-main" style="font-weight: 400;">{{ item.credit_snapshot.class_name }}</div>
                    <div class="ui-cell-sub">{{ item.payment_option_title || "—" }}</div>
                </template>
                <template v-slot:[`item.amount`]="{ item }">
                    <span style="white-space: nowrap;">{{ formatMoney(item.credit_snapshot.amount, item.credit_snapshot.currency) }}</span>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <div class="d-flex align-center" style="gap: 6px;">
                        <ui-badge set="adhesion" :status="item.status" />
                        <ui-badge set="adhesion" v-if="item.suggestion && ['submitted', 'in_review'].includes(item.status)" tone="neutral" :text="item.suggestion === 'approve' ? 'Sugestão: aprovar' : 'Sugestão: revisar'" />
                        <ui-badge set="adhesion" v-if="!item.is_current_version && item.status !== 'cancelled'" tone="neutral" text="Versão anterior" />
                    </div>
                </template>
                <template v-slot:[`item.submitted_at`]="{ item }">{{ item.submitted_at ? formatDate(item.submitted_at) : "—" }}</template>
                <template v-slot:[`item.updated_at`]="{ item }"><span style="white-space: nowrap;">{{ formatDateTime(item.reviewed_at || item.submitted_at || item.generated_at) }}</span></template>
                <template v-slot:[`item.open`]><v-icon small class="ui-cell-chevron">mdi-chevron-right</v-icon></template>
            </v-data-table>
            <ui-pagination :total="visibleItems.length" :page.sync="page" :items-per-page.sync="itemsPerPage" />
        </div>

        <v-navigation-drawer v-model="detail.open" right fixed temporary width="640" class="ui-sheet">
            <div v-if="detail.term" class="ui-sheet__inner">
                <header class="ui-sheet__header">
                    <div>
                        <div class="ui-small ui-muted">Termo <span class="ui-mono">{{ detail.term.document_code }}</span></div>
                        <div class="d-flex align-center flex-wrap mt-1" style="gap: 8px;">
                            <span class="ui-section-title">{{ detail.term.credit_snapshot.name }}</span>
                            <ui-badge set="adhesion" :status="detail.term.status" />
                        </div>
                        <div class="ui-small ui-muted mt-1">
                            Emitido {{ formatDateTime(detail.term.generated_at) }} · termo v{{ detail.term.template ? detail.term.template.version : '—' }} · plano v{{ detail.term.plan ? detail.term.plan.version : '—' }}
                        </div>
                    </div>
                    <v-btn icon @click="detail.open = false"><v-icon>mdi-close</v-icon></v-btn>
                </header>

                <div class="ui-sheet__body">
                    <v-alert v-if="creditDiverges" type="warning" outlined dense>
                        O crédito atual no QGC diverge do crédito do termo. Use "Conferir com o QGC" para cancelar a adesão e avisar o credor.
                    </v-alert>

                    <div class="ui-sheet__section">
                        <div class="ui-label">Crédito na emissão</div>
                        <dl class="ui-kv">
                            <dt>Credor</dt><dd>{{ detail.term.credit_snapshot.name }} · {{ formatRegistration(detail.term.credit_snapshot.registration) || "—" }}</dd>
                            <dt>Classe</dt><dd>{{ detail.term.credit_snapshot.class_name }}</dd>
                            <dt>Valor</dt><dd class="font-weight-medium">{{ formatMoney(detail.term.credit_snapshot.amount, detail.term.credit_snapshot.currency) }}</dd>
                            <dt>Devedora</dt><dd>{{ detail.term.credit_snapshot.debtor }}</dd>
                            <dt>Opção escolhida</dt><dd>{{ detail.term.payment_option ? detail.term.payment_option.title : "—" }}</dd>
                        </dl>
                    </div>

                    <div class="ui-sheet__section">
                        <div class="ui-label">Emissão e arquivos</div>
                        <dl class="ui-kv">
                            <dt>Emitido por</dt><dd>{{ detail.term.user ? detail.term.user.name : "—" }} ({{ detail.term.user && detail.term.user.type === 2 ? "procurador" : "credor" }})</dd>
                            <dt>Leitura e aceite</dt><dd>{{ formatDateTime(detail.term.accepted_at) }} · IP {{ detail.term.accept_ip || "—" }}</dd>
                            <template v-if="detail.term.procurator_association">
                                <dt>Procuração</dt>
                                <dd>
                                    {{ (detail.term.procurator_association.powers || []).join(", ") || "sem poderes informados" }}
                                    <a v-if="detail.term.procurator_association.procuration_url" :href="detail.term.procurator_association.procuration_url" target="_blank" class="ml-1">abrir</a>
                                </dd>
                            </template>
                        </dl>
                        <div class="d-flex mt-3" style="gap: 8px;">
                            <v-btn small outlined @click="file('generated')"><v-icon left small>mdi-file-pdf-box</v-icon>Termo emitido</v-btn>
                            <v-btn v-if="detail.term.submitted_at" small outlined @click="file('submitted')"><v-icon left small>mdi-file-sign</v-icon>Termo assinado</v-btn>
                        </div>
                    </div>

                    <div v-if="report" class="ui-sheet__section">
                        <div class="ui-label">Validação da assinatura</div>
                        <div v-if="report.signer" class="mb-2" style="font-size: 13.5px;">
                            <span class="font-weight-medium">{{ report.signer.name }}</span> · CPF {{ report.signer.cpf_masked }} · {{ formatDateTime(report.signing_time) }}
                            <div class="ui-small ui-muted">{{ report.signer.issuer }}</div>
                        </div>
                        <v-alert v-if="report.message" type="error" outlined dense>{{ report.message }}</v-alert>
                        <ul class="ui-checks">
                            <li v-for="check in report.checks" :key="check.key">
                                <v-icon small :color="check.ok === null ? '#a1a1aa' : check.ok ? '#16a34a' : '#dc2626'">
                                    {{ check.ok === null ? 'mdi-minus-circle-outline' : check.ok ? 'mdi-check-circle' : 'mdi-close-circle' }}
                                </v-icon>
                                <div>
                                    <div>{{ checkLabels[check.key] || check.key }}</div>
                                    <div class="ui-small ui-muted">{{ check.detail }}</div>
                                </div>
                            </li>
                        </ul>
                        <div v-if="report.suggestion" class="ui-suggestion mt-3" :class="report.suggestion.recommendation === 'approve' ? 'ui-suggestion--ok' : 'ui-suggestion--warn'">
                            <div class="font-weight-medium">{{ report.suggestion.recommendation === 'approve' ? 'Sugestão: aprovar' : 'Sugestão: revisar antes de decidir' }}</div>
                            <ul v-if="report.suggestion.reasons.length" class="mb-0 mt-1"><li v-for="reason in report.suggestion.reasons" :key="reason">{{ reason }}</li></ul>
                        </div>
                    </div>

                    <div v-if="detail.term.company" class="ui-sheet__section">
                        <div class="ui-label">Cadastro da empresa</div>
                        <dl class="ui-kv">
                            <dt>Representante</dt><dd>{{ detail.term.company.legal_rep_name || "—" }} · {{ detail.term.company.legal_rep_role || "—" }}</dd>
                            <dt>CPF</dt><dd>{{ formatRegistration(detail.term.company.legal_rep_registration) || "—" }}</dd>
                        </dl>
                        <div v-for="doc in detail.term.company.documents" :key="doc.id" class="pa-doc-row">
                            <v-icon small color="#71717a">mdi-file-document-outline</v-icon>
                            <span class="ui-muted">{{ doc.type_label }}</span>
                            <a :href="doc.url" target="_blank">{{ doc.name }}</a>
                        </div>
                        <div v-if="!detail.term.company.documents.length" class="ui-small" style="color: #b91c1c;">Nenhum documento societário enviado.</div>
                    </div>

                    <div v-if="detail.term.rejection_reason || detail.term.revoke_reason || detail.term.cancel_reason" class="ui-sheet__section">
                        <div class="ui-label">Histórico</div>
                        <div v-if="detail.term.rejection_reason"><span class="font-weight-medium">Rejeição:</span> {{ detail.term.rejection_reason }}</div>
                        <div v-if="detail.term.revoke_reason"><span class="font-weight-medium">Revogação:</span> {{ detail.term.revoke_reason }}</div>
                        <div v-if="detail.term.cancel_reason"><span class="font-weight-medium">Cancelamento:</span> {{ cancelLabels[detail.term.cancel_reason] || detail.term.cancel_reason }}</div>
                    </div>

                    <div v-if="action.kind" class="ui-sheet__section">
                        <v-textarea v-model="action.reason" :label="action.kind === 'reject' ? 'Motivo da rejeição (enviado ao credor)' : 'Motivo da revogação (enviado ao credor)'"
                            outlined rows="3" auto-grow maxlength="1000" counter autofocus hide-details="auto"></v-textarea>
                    </div>
                </div>

                <footer v-if="canReview || detail.term.status === 'approved'" class="ui-sheet__footer">
                    <template v-if="action.kind">
                        <v-btn outlined @click="action = { kind: null, reason: '' }">Cancelar</v-btn>
                        <v-btn color="error" depressed :disabled="!action.reason.trim()" :loading="busy === action.kind" @click="confirmAction">
                            {{ action.kind === 'reject' ? 'Rejeitar termo' : 'Revogar adesão' }}
                        </v-btn>
                    </template>
                    <template v-else>
                        <v-btn v-if="canReview && $allowed('plan.terms')" outlined class="ui-btn-danger" @click="action = { kind: 'reject', reason: '' }">Rejeitar</v-btn>
                        <v-btn v-if="(canReview) && $allowed('plan.terms')" color="primary" depressed :loading="busy === 'approve'" @click="approve">Aprovar adesão</v-btn>
                        <v-btn v-if="detail.term.status === 'approved' && $allowed('plan.terms')" outlined class="ui-btn-danger" @click="action = { kind: 'revoke', reason: '' }">Revogar adesão</v-btn>
                    </template>
                </footer>
            </div>
        </v-navigation-drawer>
    </div>
</template>

<script>
import axios from "axios";
import { errorMessage, formatDateTime } from "./plan-adhesion.util";

const CHECK_LABELS = {
    no_signature: "Assinatura digital presente",
    single_signature: "Uma única assinatura",
    byte_range: "Assinatura cobre o arquivo inteiro",
    original_hash: "PDF emitido preservado (SHA-256)",
    increment: "Só assinatura acrescentada",
    cms: "Assinatura criptográfica válida",
    govbr: "Emitida pelo GOV.br",
    signing_time_present: "Data da assinatura presente",
    chain: "Cadeia GOV.br confiável",
    validity: "Certificado válido na data",
    signing_time: "Assinado após a emissão do termo",
    revocation: "Certificado não revogado (LCR)",
    cpf_present: "CPF no certificado",
    cpf_match: "CPF do credor/procurador",
};

// Pilulas de status: o filtro de status e feito aqui, sobre a lista ja
// filtrada pelo servidor, para mostrar a quantidade de cada grupo.
const STATUS_TABS = [
    { value: "all", text: "Todas", statuses: null },
    { value: "review", text: "Em análise", tone: "warning", statuses: ["submitted", "in_review"] },
    { value: "approved", text: "Aprovadas", tone: "success", statuses: ["approved"] },
    { value: "rejected", text: "Rejeitadas", tone: "danger", statuses: ["rejected"] },
    { value: "awaiting", text: "Aguardando assinatura", tone: "neutral", statuses: ["draft", "awaiting_signature"] },
    { value: "closed", text: "Revogadas e canceladas", tone: "neutral", statuses: ["revoked", "cancelled"] },
];

const CANCEL_LABELS = {
    replaced: "Substituído por novo termo",
    template_superseded: "Nova versão do termo publicada",
    plan_superseded: "Nova versão do plano publicada",
    credit_changed: "Crédito alterado no QGC",
};

// Aba "Adesoes" do AJ: lista de termos, relatorio da validacao GOV.br,
// sugestao para PJ e as decisoes (aprovar, rejeitar, revogar).
export default {
    name: "PlanAdhesionTermsReview",


    props: {
        campaignId: { type: [Number, String], required: true },
        classNames: { type: Object, default: () => ({}) },
    },

    data: () => ({
        items: [],
        loading: false,
        reconciling: false,
        busy: null,
        filter: { search: "", class_type: null, signer_kind: null, current: true },
        statusTab: "all",
        page: 1,
        itemsPerPage: 10,
        searchTimer: null,
        detail: { open: false, term: null },
        action: { kind: null, reason: "" },
        checkLabels: CHECK_LABELS,
        cancelLabels: CANCEL_LABELS,
        signerItems: [
            { text: "PF e PJ", value: null },
            { text: "Pessoa física", value: "pf" },
            { text: "Pessoa jurídica", value: "pj" },
        ],
        headers: [
            { text: "Credor", value: "creditor", cellClass: "ui-cell-wide" },
            { text: "Classe e opção", value: "class_option", cellClass: "ui-nowrap" },
            { text: "Crédito", value: "amount", align: "end", cellClass: "ui-nowrap" },
            { text: "Status", value: "status", cellClass: "ui-nowrap" },
            { text: "Assinado em", value: "submitted_at", cellClass: "ui-nowrap" },
            { text: "Atualizado em", value: "updated_at", cellClass: "ui-nowrap" },
            { text: "", value: "open", width: 40, align: "end" },
        ],
    }),

    computed: {
        classItems() {
            return [{ value: null, text: "Todas as classes" }, ...Object.entries(this.classNames).map(([value, text]) => ({ value: Number(value), text }))];
        },
        pillItems() {
            return STATUS_TABS.map((tab) => ({ ...tab, count: tab.statuses ? this.items.filter((item) => tab.statuses.includes(item.status)).length : this.items.length }));
        },
        visibleItems() {
            const tab = STATUS_TABS.find((entry) => entry.value === this.statusTab);
            return tab && tab.statuses ? this.items.filter((item) => tab.statuses.includes(item.status)) : this.items;
        },
        hasFilters() {
            return !!(this.filter.search || this.filter.class_type || this.filter.signer_kind || !this.filter.current || this.statusTab !== "all");
        },
        report() {
            return this.detail.term && this.detail.term.signature_report;
        },
        canReview() {
            const term = this.detail.term;
            return term && ["in_review", "submitted"].includes(term.status) && term.is_current_version;
        },
        creditDiverges() {
            const term = this.detail.term;
            if (!term || !term.current_credit || ["cancelled", "revoked", "rejected"].includes(term.status)) return false;
            const credit = term.current_credit;
            const snapshot = term.credit_snapshot;
            return credit.deleted || Math.round(credit.amount * 100) !== Math.round(Number(snapshot.amount) * 100)
                || credit.type !== Number(snapshot.class_type) || credit.currency !== (Number(snapshot.currency) || 1);
        },
    },

    watch: {
        statusTab() {
            this.page = 1;
        },
        "filter.search"() {
            clearTimeout(this.searchTimer);
            this.searchTimer = setTimeout(() => this.reload(), 400);
        },
    },

    created() {
        this.load();
    },

    beforeDestroy() {
        clearTimeout(this.searchTimer);
    },

    methods: {
        formatDateTime,

        formatDate(value) {
            return formatDateTime(value).split(",")[0];
        },

        clearFilters() {
            clearTimeout(this.searchTimer);
            this.filter = { search: "", class_type: null, signer_kind: null, current: true };
            this.statusTab = "all";
            this.$nextTick(() => {
                clearTimeout(this.searchTimer);
                this.reload();
            });
        },

        formatMoney(amount, currency) {
            const symbol = { 1: "R$", 2: "US$", 3: "€" }[Number(currency)] || "R$";
            return `${symbol} ${Number(amount || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },

        formatRegistration(value) {
            const digits = String(value || "").replace(/\D/g, "");
            if (digits.length === 11) return digits.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
            if (digits.length === 14) return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
            return value || "";
        },

        notify(message, type = "success") {
            this.$dialog.notify[type](message, { position: "top-right", timeout: type === "error" ? 5000 : 2500 });
        },

        reload() {
            return this.load({ resetPage: true });
        },

        // Depois de aprovar/rejeitar a lista recarrega na mesma pagina.
        async load({ resetPage = false } = {}) {
            this.loading = true;
            try {
                const params = {
                    search: this.filter.search || undefined,
                    class_type: this.filter.class_type || undefined,
                    signer_kind: this.filter.signer_kind || undefined,
                    current: this.filter.current ? "true" : undefined,
                };
                const result = await axios.get(`/api/admin/plan-adhesions/${this.campaignId}/terms`, { params });
                this.items = result.data.content;
                const pageCount = Math.max(1, Math.ceil(this.visibleItems.length / this.itemsPerPage));
                this.page = resetPage ? 1 : Math.min(this.page, pageCount);
            } catch (error) {
                this.notify(errorMessage(error), "error");
            } finally {
                this.loading = false;
            }
        },

        async open(item) {
            try {
                const result = await axios.get(`/api/admin/plan-adhesion-terms/${item.id}`);
                this.detail = { open: true, term: result.data.content };
                this.action = { kind: null, reason: "" };
            } catch (error) {
                this.notify(errorMessage(error), "error");
            }
        },

        async file(kind) {
            try {
                const result = await axios.get(`/api/admin/plan-adhesion-terms/${this.detail.term.id}/file`, { params: { kind } });
                window.open(result.data.content.url, "_blank");
            } catch (error) {
                this.notify(errorMessage(error), "error");
            }
        },

        async approve() {
            this.busy = "approve";
            try {
                const result = await axios.post(`/api/admin/plan-adhesion-terms/${this.detail.term.id}/approve`);
                this.detail.term = result.data.content;
                this.notify("Adesão aprovada. O credor foi avisado por e-mail.");
                this.$emit("changed");
                this.load();
            } catch (error) {
                this.notify(errorMessage(error), "error");
            } finally {
                this.busy = null;
            }
        },

        async confirmAction() {
            const kind = this.action.kind;
            this.busy = kind;
            try {
                const result = await axios.post(`/api/admin/plan-adhesion-terms/${this.detail.term.id}/${kind}`, { reason: this.action.reason.trim() });
                this.detail.term = result.data.content;
                this.action = { kind: null, reason: "" };
                this.notify(kind === "reject" ? "Termo rejeitado. O credor pode reenviar." : "Adesão revogada.");
                this.$emit("changed");
                this.load();
            } catch (error) {
                this.notify(errorMessage(error), "error");
            } finally {
                this.busy = null;
            }
        },

        async reconcile() {
            this.reconciling = true;
            try {
                const result = await axios.post(`/api/admin/plan-adhesions/${this.campaignId}/reconcile`);
                const { checked, relinked, cancelled } = result.data.content;
                this.notify(`${checked} termo(s) conferido(s): ${cancelled} cancelado(s) por alteração de crédito, ${relinked} religado(s) após reconsolidação.`);
                this.load();
            } catch (error) {
                this.notify(errorMessage(error), "error");
            } finally {
                this.reconciling = false;
            }
        },
    },
};
</script>
